// ThemePackService.js

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';
import { generateKey } from 'utilities/Keys';
import {
  createThemePackFromBuiltin,
  duplicateThemePack,
  normalizeThemePack,
} from './themePackModel';

const THEME_PACKS_COLLECTION = collections.themePacks;

function themePackDocument(packKey) {
  return doc(db, THEME_PACKS_COLLECTION, packKey);
}

function themePacksCollection() {
  return collection(db, THEME_PACKS_COLLECTION);
}

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== undefined)
      .map((entry) => stripUndefinedDeep(entry));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined) {
      return accumulator;
    }
    accumulator[key] = stripUndefinedDeep(entryValue);
    return accumulator;
  }, {});
}

/**
 * Subscribe to a single personal theme pack document.
 *
 * @param {string} packKey
 * @param {(pack: object|null) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToThemePack(packKey, callback, onError) {
  const normalizedPackKey = String(packKey || '').trim();

  if (!normalizedPackKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    themePackDocument(normalizedPackKey),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback(normalizeThemePack({
        id: snapshot.id,
        key: snapshot.id,
        ...snapshot.data(),
      }));
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('[ThemePackService] subscribeToThemePack error:', error);
      }
      callback(null);
    },
  );
}

/**
 * Subscribe to the signed-in user's personal theme library.
 *
 * @param {string} userKey
 * @param {(packs: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToUserThemePacks(userKey, callback, onError) {
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedUserKey || typeof callback !== 'function') {
    return () => {};
  }

  const packsQuery = query(
    themePacksCollection(),
    where('userKey', '==', normalizedUserKey),
  );

  return onSnapshot(
    packsQuery,
    (snapshot) => {
      const packs = snapshot.docs
        .map((docSnap) => normalizeThemePack({
          id: docSnap.id,
          key: docSnap.id,
          ...docSnap.data(),
        }))
        .filter(Boolean)
        .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));

      callback(packs);
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('[ThemePackService] subscribe error:', error);
      }
      callback([]);
    },
  );
}

/**
 * Create a personal library pack forked from the builtin default.
 *
 * @param {{ userKey: string, name?: string }} options
 * @returns {Promise<object>}
 */
export async function createPersonalThemePack({ userKey, name } = {}) {
  const normalizedUserKey = String(userKey || '').trim();
  if (!normalizedUserKey) {
    throw new Error('userKey is required');
  }

  const packKey = generateKey();
  const pack = createThemePackFromBuiltin({
    key: packKey,
    name: name || 'Untitled theme',
    userKey: normalizedUserKey,
  });

  const payload = stripUndefinedDeep({
    ...pack,
    dateCreated: serverTimestamp(),
    dateModified: serverTimestamp(),
  });

  await setDoc(themePackDocument(packKey), payload);
  return normalizeThemePack(pack);
}

/**
 * Duplicate an existing personal library pack.
 *
 * @param {{ userKey: string, sourcePack: object, name?: string }} options
 * @returns {Promise<object>}
 */
export async function duplicatePersonalThemePack({ userKey, sourcePack, name } = {}) {
  const normalizedUserKey = String(userKey || '').trim();
  if (!normalizedUserKey) {
    throw new Error('userKey is required');
  }
  if (!sourcePack) {
    throw new Error('sourcePack is required');
  }

  const packKey = generateKey();
  const pack = duplicateThemePack(sourcePack, {
    key: packKey,
    name,
    userKey: normalizedUserKey,
  });

  const payload = stripUndefinedDeep({
    ...pack,
    dateCreated: serverTimestamp(),
    dateModified: serverTimestamp(),
  });

  await setDoc(themePackDocument(packKey), payload);
  return normalizeThemePack(pack);
}

/**
 * Save edits to a personal library pack.
 *
 * @param {{ userKey: string, pack: object }} options
 * @returns {Promise<object>}
 */
export async function savePersonalThemePack({ userKey, pack } = {}) {
  const normalizedUserKey = String(userKey || '').trim();
  const normalized = normalizeThemePack(pack);

  if (!normalizedUserKey) {
    throw new Error('userKey is required');
  }
  if (!normalized?.key) {
    throw new Error('pack.key is required');
  }
  if (String(normalized.userKey || '').trim() !== normalizedUserKey) {
    throw new Error('Cannot save a theme pack you do not own');
  }

  const payload = stripUndefinedDeep({
    ...normalized,
    userKey: normalizedUserKey,
    dateModified: serverTimestamp(),
  });

  await setDoc(themePackDocument(normalized.key), payload, { merge: true });
  return normalized;
}

/**
 * Delete a personal library pack.
 *
 * @param {{ userKey: string, packKey: string }} options
 * @returns {Promise<void>}
 */
export async function deletePersonalThemePack({ userKey, packKey } = {}) {
  const normalizedUserKey = String(userKey || '').trim();
  const normalizedPackKey = String(packKey || '').trim();

  if (!normalizedUserKey) {
    throw new Error('userKey is required');
  }
  if (!normalizedPackKey) {
    throw new Error('packKey is required');
  }

  await deleteDoc(themePackDocument(normalizedPackKey));
}
